// src/console/pages/races/racesNav.ts — where "Growth › Points races" lives in
// the console, and the race page's tab row.
//
// ── REGISTERING THE DOMAIN (three edits outside this folder) ──────────────
// The console reads ONE nav source (src/console/nav.ts) for the sidebar, the
// router and the palette, and derives every sidebar gate from the route rules
// (src/config/adminFeatureAccess.ts). So the race needs exactly these:
//
//   1. nav.ts — add POINTS_RACES_DOMAIN to DOMAINS, and a "Growth" group to
//      NAV_MODEL:  { title: POINTS_RACES_NAV_GROUP, items: [domain("points-races")] }
//      placed after "Customers" (the artboard draws Growth between Money and
//      System). `idForPath` already resolves /console/points-races/* to it.
//   2. adminFeatureAccess.ts — add POINTS_RACES_ROUTE_RULE to
//      ADMIN_FEATURE_ROUTE_RULES ABOVE the `admin.dashboard` "/console/*"
//      catch-all (first match wins). Without it the race is open to every
//      operator and the sidebar shows it to everyone.
//   3. ConsoleApp.tsx — two routes, static before dynamic like the rest of
//      the file:
//        <Route path="points-races" element={<RaceIndexRedirect />} />
//        <Route path="points-races/:slug" element={<RaceDetailPage />} />
//      The races list (ListExplorer) is not designed yet; until it is, the
//      domain root opens pickRaceToOpen()'s race. Swap the redirect for the
//      list when it exists.
//
// The artboard's "1 live" sidebar badge is `useNavBadges` returning
// `{ "points-races": <live race count> }` — a follow-up, it needs a count
// source (GET /admin/points-campaigns?status=live).
import type { ConsoleRoute } from "../../nav";
import type { AdminFeatureRouteRule } from "../../../config/adminFeatureAccess";
import { RACE_CAPABILITY } from "./raceAccess";
import type { AdminCampaignStatus, AdminRaceCampaign } from "./raceAdminTypes";

export const POINTS_RACES_DOMAIN: ConsoleRoute = {
  id: "points-races",
  label: "Points races",
  icon: "zap",
  keywords:
    "88 race points race dream boys referral leaderboard power hour boost equaliser scoring",
};

export const POINTS_RACES_NAV_GROUP = "Growth";

/**
 * `campaign.points.view` opens the race; the writes inside it check their own
 * capability (raceAccess.ts). Gating the route on a write key would hide the
 * console from finance and compliance, who read it (spec §6.9, D12).
 */
export const POINTS_RACES_ROUTE_RULE: AdminFeatureRouteRule = {
  featureKeys: [RACE_CAPABILITY.view],
  patterns: ["/console/points-races", "/console/points-races/*"],
};

const OPEN_FIRST: AdminCampaignStatus[] = ["live", "paused", "scheduled"];
const openRank = (status: AdminCampaignStatus): number =>
  OPEN_FIRST.includes(status) ? 0 : status === "draft" ? 1 : 2;

/** The race the domain root opens: the newest non-archived one, running or scheduled before draft before ended. */
export const pickRaceToOpen = (
  races: readonly AdminRaceCampaign[]
): AdminRaceCampaign | null =>
  races
    .filter((r) => r.status !== "archived")
    .sort(
      (a, b) =>
        openRank(a.status) - openRank(b.status) ||
        Date.parse(b.startsAt) - Date.parse(a.startsAt)
    )[0] ?? null;

/** The ObjectDetail tab row, in the artboard's order. */
export const RACE_TAB_KEYS = [
  "overview",
  "race-settings",
  "boosts",
  "leaderboards",
  "participants",
  "close",
  "settings",
] as const;

export type RaceTabKey = (typeof RACE_TAB_KEYS)[number];

export const RACE_TAB_LABEL: Record<RaceTabKey, string> = {
  overview: "Overview",
  "race-settings": "Race settings",
  boosts: "Boosts",
  leaderboards: "Leaderboards",
  participants: "Participants",
  close: "Close and prizes",
  settings: "Settings",
};

/** The tabs this build ships. The others render a placeholder until they are designed and built. */
export const BUILT_RACE_TABS: readonly RaceTabKey[] = [
  "race-settings",
  "boosts",
  "participants",
  "close",
  "settings",
];

/**
 * Lands on Race settings, not Overview: Overview is not in the one-week build,
 * and a default that opens a placeholder reads as a broken page. Move this to
 * "overview" when that tab ships.
 */
export const DEFAULT_RACE_TAB: RaceTabKey = "race-settings";

export const isRaceTab = (v: string | null): v is RaceTabKey =>
  v !== null && (RACE_TAB_KEYS as readonly string[]).includes(v);

export const parseRaceTab = (v: string | null): RaceTabKey =>
  isRaceTab(v) ? v : DEFAULT_RACE_TAB;

export const racesListPath = (): string => "/console/points-races";

/** "/console/points-races/88-race-s2?tab=boosts" — the default tab carries no ?tab=. */
export const racePath = (slug: string, tab?: RaceTabKey): string =>
  `/console/points-races/${encodeURIComponent(slug)}` +
  (tab && tab !== DEFAULT_RACE_TAB ? `?tab=${tab}` : "");

/**
 * The race's emergency stop, as System › Launch controls keys it. While it is
 * ON nothing scores and every board is read-only, whatever a race's own status
 * says, so the race page reads it too.
 */
export const RACE_FREEZE_CONTROL_KEY = "campaigns.points.global_freeze";

/**
 * The emergency freeze lives in System › Launch controls. The link opens that
 * tab with the freeze's own card in view: it used to land on System's first
 * tab, the Audit log, and leave the operator to find the rest.
 */
export const LAUNCH_CONTROLS_PATH = `/console/system?tab=launch&control=${encodeURIComponent(
  RACE_FREEZE_CONTROL_KEY
)}`;

/**
 * Where "Preview as racer" opens the race as a racer sees it. Not decided yet
 * (the web app's race view has no route), so it is null and the page hides the
 * button rather than offer one that does nothing. Set it to a function of the
 * slug — e.g. the web app's race view with the operator in the preview cohort —
 * and the button appears.
 */
export const RACER_PREVIEW_URL: ((slug: string) => string) | null = null;
